import { Text, SimpleGrid, Box, Button, Flex } from '@chakra-ui/react';

import { useAppDispatch } from 'hooks';

import FormInput from 'components/fields/home/FormInput';
import SelectInput from 'components/fields/home/SelectInput';
import Divider from 'views/home_form/components/Divider';

import {
  changeDwellingLiabilityExtensionsExclusionsItem,
  addDwellingLiabilityExtensionsExclusionsItem,
  removeDwellingLiabilityExtensionsExclusionsItem,
} from 'reducers/homePersonalFormReducer';

import { SECTIONS } from 'views/home_form/constants';

import type { APIPersonalHomeApplicationForm } from 'types/api.types';
import useIsBinderDisabled from 'hooks/useFormBinderDisabled';

interface LiabilityExtensionsExclusionsProps {
  dwelling: APIPersonalHomeApplicationForm['dwellings'][0];
  index: number;
}

const LiabilityExtensionsExclusions = ({
  dwelling,
  index,
}: LiabilityExtensionsExclusionsProps) => {
  const dispatch = useAppDispatch();
  const isDisabled = useIsBinderDisabled('homePersonalForm');

  const onLiabilityExtensionItemChange = (event: any, itemIndex: number) => {
    const { name, value } = event.target;

    // Validate numeric fields to allow only numbers with up to 2 decimal places
    if (
      ['amount_of_insurance', 'deductible', 'estimated_premium'].includes(name)
    ) {
      const numericRegex = /^\d*\.?\d{0,2}$/;
      if (value !== '' && !numericRegex.test(value)) {
        return; // Don't update if the value doesn't match the pattern
      }
    }

    dispatch(
      changeDwellingLiabilityExtensionsExclusionsItem({
        dwellingIndex: index,
        itemIndex,
        name,
        value,
      }),
    );
  };

  const handleAddLiabilityExtensionItem = () => {
    if (
      !dwelling.liability_extensions_exclusions ||
      dwelling.liability_extensions_exclusions.length < 4
    ) {
      dispatch(addDwellingLiabilityExtensionsExclusionsItem(index));
    }
  };

  const handleRemoveLiabilityExtensionItem = (itemIndex: number) => {
    dispatch(
      removeDwellingLiabilityExtensionsExclusionsItem({
        dwellingIndex: index,
        itemIndex,
      }),
    );
  };

  return (
    <Box>
      <Divider mb="15px">Liability Extensions And Exclusions</Divider>

      {dwelling.liability_extensions_exclusions &&
      dwelling.liability_extensions_exclusions.length > 0 ? (
        <>
          <Text fontWeight="medium" fontSize="md" mb="15px">
            Liability Extension Items
          </Text>

          {dwelling.liability_extensions_exclusions.map(
            (item: any, itemIndex: number) => (
              <Box
                key={itemIndex}
                p="15px"
                border="1px"
                borderColor="gray.200"
                borderRadius="md"
                mt={itemIndex === 0 ? '0' : '15px'}
              >
                <Flex justify="space-between" align="center" mb="15px">
                  <Text fontWeight="medium" fontSize="sm">
                    Liability Extension Item {itemIndex + 1}
                  </Text>
                  <Button
                    size="xs"
                    colorScheme="red"
                    variant="outline"
                    onClick={() =>
                      handleRemoveLiabilityExtensionItem(itemIndex)
                    }
                  >
                    Remove
                  </Button>
                </Flex>

                <SimpleGrid columns={6} spacing="20px" mb="15px">
                  <FormInput
                    section={SECTIONS.DWELLINGS}
                    label="Description"
                    name="liability_coverage_description"
                    infoText="Liability coverage description"
                    value={item.liability_coverage_description}
                    onChange={(event: any) =>
                      onLiabilityExtensionItemChange(event, itemIndex)
                    }
                    required
                  />

                  <SelectInput
                    section={SECTIONS.DWELLINGS}
                    label="Requested/declined"
                    name="requested_declined"
                    options={[
                      { value: '', label: 'Select...' },
                      { value: 'requested', label: 'Requested' },
                      { value: 'declined', label: 'Declined' },
                    ]}
                    value={item.requested_declined}
                    onChange={(event: any) =>
                      onLiabilityExtensionItemChange(event, itemIndex)
                    }
                    required
                  />

                  <FormInput
                    section={SECTIONS.DWELLINGS}
                    label="Amount of insurance"
                    name="amount_of_insurance"
                    value={item.amount_of_insurance}
                    onChange={(event: any) =>
                      onLiabilityExtensionItemChange(event, itemIndex)
                    }
                    required
                  />

                  <FormInput
                    section={SECTIONS.DWELLINGS}
                    label="Deductible"
                    name="deductible"
                    value={item.deductible}
                    onChange={(event: any) =>
                      onLiabilityExtensionItemChange(event, itemIndex)
                    }
                    required
                  />

                  <FormInput
                    section={SECTIONS.DWELLINGS}
                    label="Deductible type"
                    name="deductible_type"
                    value={item.deductible_type}
                    onChange={(event: any) =>
                      onLiabilityExtensionItemChange(event, itemIndex)
                    }
                    required
                  />

                  <FormInput
                    section={SECTIONS.DWELLINGS}
                    label="Estimated premium"
                    name="estimated_premium"
                    value={item.estimated_premium}
                    onChange={(event: any) =>
                      onLiabilityExtensionItemChange(event, itemIndex)
                    }
                    required
                  />
                </SimpleGrid>
              </Box>
            ),
          )}
        </>
      ) : (
        <Text fontSize="md" color="gray.600" fontStyle="italic">
          No liability extension items added
        </Text>
      )}

      {(!dwelling.liability_extensions_exclusions ||
        dwelling.liability_extensions_exclusions.length < 4) && (
        <Box mt="15px">
          <Button
            size="sm"
            colorScheme="blue"
            variant="outline"
            onClick={handleAddLiabilityExtensionItem}
            disabled={isDisabled}
          >
            Add Liability Extension Item (
            {dwelling.liability_extensions_exclusions?.length || 0}/4)
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default LiabilityExtensionsExclusions;
